import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useChatLayout } from '../context/ChatLayoutContext';
import { motion } from 'framer-motion';
import { Copy, Gift, Users, Coins, CheckCircle, Wallet, Sparkles, TrendingUp, KeySquare, Trophy, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const formatAmount = (amount) => {
  if (amount === undefined || amount === null || amount === 0) return '0';
  if (amount >= 1000000) return amount.toLocaleString();
  if (amount >= 1000) return parseFloat((amount / 1000).toFixed(2)) + 'K';
  return amount.toString();
};

export const Affiliates = () => {
  const { user, token, isAuthenticated } = useAuth();
  
  const [redeemCode, setRedeemCode] = useState('');
  const [createCodeInput, setCreateCodeInput] = useState('');
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState({ top_10: [], current_user: null });
  
  const [isClaiming, setIsClaiming] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  // 💬 Aç/kapa durumu paylaşımlı: sayfa değişse de korunur.
  const { chatOpen } = useChatLayout();

  const redeemLock = useRef(false);

  useEffect(() => {
    if (isAuthenticated && token) {
      axios.get(`${API}/game/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setStats(res.data)).catch(() => {});

      axios.get(`${API}/affiliate/leaderboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if(res.data.success) {
            setLeaderboard({ top_10: res.data.top_10, current_user: res.data.current_user });
        }
      }).catch(() => {});
    }
  }, [isAuthenticated, token]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0E0F11] pt-32 pb-12 flex items-center justify-center">
        <div className="text-center">
          <Gift className="w-16 h-16 text-[#B059E8] mx-auto mb-4 opacity-50" />
          <h2 className="text-2xl font-heading font-bold text-white mb-2">Login Required</h2>
          <p className="text-zinc-400">Please log in to view your affiliate dashboard.</p>
        </div>
      </div>
    );
  }

  const handleCopyCode = () => {
    if (user?.affiliate_code) {
      navigator.clipboard.writeText(user.affiliate_code);
      toast.success("Affiliate code copied to clipboard!");
    }
  };

  const handleCreateCode = async () => {
    if (!createCodeInput.trim() || createCodeInput.length < 3) return toast.error("Code must be at least 3 characters!");
    setIsCreating(true);
    try {
      const response = await axios.post(`${API}/affiliate/create`, { code: createCodeInput }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create code.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleClaimVault = async () => {
    if (isClaiming) return;
    setIsClaiming(true);
    try {
      const response = await axios.post(`${API}/affiliate/claim`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setTimeout(() => window.location.reload(), 1500); 
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to claim vault.");
    } finally {
      setIsClaiming(false);
    }
  };

  const handleRedeemCode = async () => {
    if (!redeemCode.trim()) return toast.error("Please enter a code!");

    if (redeemLock.current) return;
    redeemLock.current = true;
    setIsRedeeming(true);

    try {
      const response = await axios.post(`${API}/affiliate/redeem`, {
        code: redeemCode.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(response.data.message);
      setTimeout(() => window.location.reload(), 1500);

    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to redeem code.");
    } finally {
      setIsRedeeming(false);
      redeemLock.current = false;
    }
  };

  const vaultAmount = user?.affiliate_vault || 0;
  const totalEarned = user?.total_affiliate_earned || 0;
  const hasUsedCode = user?.used_affiliate === true;

  return (
    <div className="min-h-screen bg-[#0E0F11] relative overflow-x-hidden pt-20 sm:pt-24 pb-12" data-testid="affiliates-page">
      <div className="max-w-[1800px] w-full mx-auto px-4 relative">
        <div className={`flex gap-6 w-full items-start transition-[padding] duration-300 ${chatOpen ? 'md:pl-[340px] lg:pl-[370px]' : ''}`}>
          
          {/* CHAT SIDEBAR */}
          {/* 💬 Sohbet App.js'te TEK kopya render ediliyor (ChatDock).
              Sayfa-içi kopya kaldırıldı: her gezinmede WebSocket kopup
              yeniden bağlanıyor ve 4 HTTP isteği tekrar atılıyordu. */}
          {/* MAIN CONTENT */}
          <main className="flex-1 min-w-0 flex flex-col gap-6">
            
            {/* HEADER */}
            <div className="text-center mb-6">
              <motion.div 
                initial={{ scale: 0 }} animate={{ scale: 1 }} 
                className="w-16 h-16 sm:w-20 sm:h-20 bg-[#B059E8]/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-[#B059E8]/20 shadow-[0_0_30px_rgba(176,89,232,0.15)]"
              >
                <Users className="w-8 h-8 sm:w-10 sm:h-10 text-[#c98bf0]" />
              </motion.div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-black text-white mb-3 uppercase tracking-wider">
                Affiliate <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#B059E8] to-[#16A34A]">Program</span>
              </h1>
              <p className="text-zinc-400 max-w-2xl mx-auto text-sm sm:text-base px-2">
                Invite your friends and earn a <span className="text-[#2ecf6e] font-bold">10% commission</span> from the house edge on every single bet they place, forever.
              </p>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              
              <div className="xl:col-span-2 space-y-6">
                
                {/* KOD KULLANMA KARTI */}
                {!hasUsedCode ? (
                  <div className="bg-gradient-to-r from-[#B059E8]/10 to-[#7b2fc4]/10 rounded-2xl border border-[#B059E8]/30 p-6 shadow-xl relative overflow-hidden">
                     <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
                        <div className="w-16 h-16 rounded-full bg-[#B059E8]/15 flex items-center justify-center flex-shrink-0 border border-[#B059E8]/30">
                          <Gift className="w-8 h-8 text-[#c98bf0]" />
                        </div>
                        <div className="flex-1 text-center md:text-left">
                          <h2 className="text-xl font-heading font-bold text-white mb-1">Support a Creator</h2>
                          <p className="text-zinc-400 text-sm mb-4">
                            Use any referral code and instantly earn <span className="text-amber-400 font-bold">$5</span> as your welcome bonus!
                            <span className="block mt-1 text-[#d29bff]/80 text-xs italic font-semibold">No one referred you? You can use code BLOXBETS.</span>
                          </p>
                          
                          <div className="flex flex-col sm:flex-row gap-2">
                            <input
                              type="text"
                              value={redeemCode}
                              onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                              placeholder="ENTER CODE"
                              className="flex-1 bg-[#15171a] border border-[#B059E8]/30 rounded-xl px-4 py-3 text-white font-bold mm-num uppercase focus:border-[#B059E8] outline-none transition-colors text-center md:text-left"
                            />
                            <button 
                              onClick={handleRedeemCode}
                              disabled={isRedeeming || !redeemCode.trim()}
                              className="bg-[#B059E8] hover:bg-[#7b2fc4] text-white px-8 py-3 rounded-xl font-bold transition-colors shadow-[0_0_14px_rgba(176,89,232,0.4)] disabled:opacity-50 whitespace-nowrap"
                            >
                              {isRedeeming ? 'Checking...' : 'Claim Bonus'}
                            </button>
                          </div>
                        </div>
                     </div>
                  </div>
                ) : (
                  <div className="bg-[#16A34A]/10 rounded-2xl border border-[#16A34A]/20 p-6 flex items-center justify-center gap-3">
                     <CheckCircle className="w-6 h-6 text-[#2ecf6e]" />
                     <span className="text-[#2ecf6e] font-bold">You have successfully supported a creator!</span>
                  </div>
                )}

                {/* İSTATİSTİKLER */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-[#1A1D20] rounded-2xl border border-[#2A2D31] p-5 shadow-xl flex flex-col justify-center items-center text-center gap-2">
                    <div className="w-12 h-12 rounded-xl bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-[#2ecf6e]" />
                    </div>
                    <div>
                      <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider mb-1">Total Earned</p>
                      <p className="text-xl mm-num font-bold text-[#2ecf6e]">
                        ${formatAmount(totalEarned)}
                      </p>
                    </div>
                  </div>
                  <div className="bg-[#1A1D20] rounded-2xl border border-[#2A2D31] p-5 shadow-xl flex flex-col justify-center items-center text-center gap-2">
                    <div className="w-12 h-12 rounded-xl bg-[#B059E8]/10 border border-[#B059E8]/20 flex items-center justify-center">
                      <Users className="w-6 h-6 text-[#c98bf0]" />
                    </div>
                    <div>
                      <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider mb-1">Total Referred</p>
                      <p className="text-xl mm-num font-bold text-[#c98bf0]">
                        {stats?.total_referred || 0}
                      </p>
                    </div>
                  </div>
                  <div className="bg-[#1A1D20] rounded-2xl border border-[#2A2D31] p-5 shadow-xl flex flex-col justify-center items-center text-center gap-2">
                    <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                      <Coins className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider mb-1">Commission Rate</p>
                      <p className="text-xl mm-num font-bold text-amber-400">10.0%</p>
                    </div>
                  </div>
                </div>

                {/* KOD OLUŞTURMA KARTI */}
                <div className="bg-[#1A1D20] rounded-2xl border border-[#2A2D31] p-6 shadow-xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-[#B059E8]/8 rounded-full blur-[80px] pointer-events-none" />
                  <h2 className="text-xl font-heading font-bold text-white mb-2">Your Referral Code</h2>
                  
                  <p className="text-zinc-400 text-sm mb-6 leading-relaxed">
                    Create a custom referral code and share it with your audience.
                    <strong className="text-[#c98bf0] block mt-1">For every user who uses your code, they receive $5 — and you earn a 10% commission forever.</strong>
                  </p>
                  
                  {user?.affiliate_code ? (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="flex-1 bg-[#15171a] border border-[#2A2D31] rounded-xl flex items-center px-4 py-3">
                        <span className="text-[#c98bf0] mm-num font-bold text-lg tracking-wider">
                          {user.affiliate_code}
                        </span>
                      </div>
                      <button 
                        onClick={handleCopyCode}
                        className="bg-[#2A2D31] hover:bg-[#33363b] text-white px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors border border-[#2A2D31]"
                      >
                        <Copy className="w-5 h-5" /> Copy Code
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-3 relative z-10">
                      <div className="flex-1 bg-[#15171a] border border-[#2A2D31] rounded-xl flex items-center px-4 py-3 focus-within:border-[#B059E8] transition-colors">
                        <KeySquare className="w-5 h-5 text-[#c98bf0] mr-3" />
                        <input
                          type="text"
                          value={createCodeInput}
                          onChange={(e) => setCreateCodeInput(e.target.value.toUpperCase())}
                          placeholder="CREATE YOUR CODE (e.g. NINJA)"
                          className="w-full bg-transparent text-white mm-num font-bold outline-none uppercase"
                        />
                      </div>
                      <button 
                        onClick={handleCreateCode}
                        disabled={isCreating || !createCodeInput.trim()}
                        className="bg-[#B059E8] hover:bg-[#7b2fc4] text-white px-8 py-3 rounded-xl font-bold transition-colors shadow-[0_0_14px_rgba(176,89,232,0.4)] disabled:opacity-50 whitespace-nowrap"
                      >
                        {isCreating ? 'Creating...' : 'Create Code'}
                      </button>
                    </div>
                  )}
                </div>

              </div>

              {/* SAĞ SÜTUN: VAULT */}
              <div className="xl:col-span-1 h-full">
                <div className="bg-gradient-to-b from-[#1A1D20] to-[#15171a] rounded-2xl border border-[#B059E8]/30 p-8 shadow-[0_0_40px_rgba(176,89,232,0.1)] flex flex-col items-center text-center h-full relative overflow-hidden">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-[#B059E8]/20 rounded-full blur-[60px] pointer-events-none" />
                  
                  <Wallet className="w-12 h-12 text-[#c98bf0] mb-4 relative z-10" />
                  <h2 className="text-2xl font-heading font-bold text-white mb-2 relative z-10">Affiliate Vault</h2>
                  <p className="text-zinc-400 text-sm mb-8 relative z-10">Available commissions to claim to your main balance.</p>
                  
                  <div className="bg-[#15171a] w-full py-6 rounded-2xl border border-[#2A2D31] mb-auto relative z-10 shadow-inner">
                    <span className="text-zinc-500 font-bold text-sm uppercase tracking-widest block mb-2">Available</span>
                    <span className={`text-4xl lg:text-5xl mm-num font-bold ${vaultAmount > 0 ? 'text-[#B059E8] drop-shadow-[0_0_15px_rgba(176,89,232,0.5)]' : 'text-zinc-600'}`}>
                      ${formatAmount(vaultAmount)}
                    </span>
                  </div>

                  <button 
                    onClick={handleClaimVault}
                    disabled={isClaiming || vaultAmount <= 0}
                    className={`w-full mt-8 py-4 rounded-xl font-heading font-bold text-xl uppercase tracking-wider transition-all shadow-xl flex items-center justify-center gap-2 relative z-10
                      ${vaultAmount > 0 
                        ? 'bg-[#16A34A] hover:bg-[#15913f] text-white animate-pulse shadow-[0_0_20px_rgba(22,163,74,0.4)]' 
                        : 'bg-[#2A2D31]/40 text-zinc-500 border border-[#2A2D31] cursor-not-allowed'}`}
                  >
                    {isClaiming ? 'Claiming...' : (
                      <>
                        <Sparkles className="w-5 h-5" /> Claim to Balance
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* ========================================== */}
            {/* LEADERBOARD (AVATAR EKLENMİŞ HALİ) */}
            {/* ========================================== */}
            <div className="bg-[#1A1D20] rounded-2xl border border-[#2A2D31] p-6 shadow-xl relative overflow-hidden">
               <h2 className="text-2xl font-heading font-bold text-white mb-6 flex items-center gap-3">
                 <div className="p-2 bg-amber-500/10 rounded-lg border border-amber-500/20">
                    <Trophy className="text-amber-400 w-6 h-6" />
                 </div>
                 Affiliate Leaderboard
               </h2>
               
               <div className="overflow-x-auto">
                 <table className="w-full text-left border-collapse min-w-[500px]">
                   <thead>
                     <tr className="border-b border-[#2A2D31]/50 text-zinc-500 text-xs uppercase tracking-widest">
                       <th className="py-3 px-4 font-bold w-20">Rank</th>
                       <th className="py-3 px-4 font-bold">Player</th>
                       <th className="py-3 px-4 font-bold text-right">Total Invited</th>
                     </tr>
                   </thead>
                   <tbody className="text-sm">
                     {leaderboard.top_10.map((player) => (
                       <tr key={player.rank} className="border-b border-[#2A2D31]/20 hover:bg-white/5 transition-colors">
                         <td className="py-4 px-4">
                           <span className={`font-black text-lg ${player.rank === 1 ? 'text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.6)]' : player.rank === 2 ? 'text-zinc-300' : player.rank === 3 ? 'text-amber-700' : 'text-zinc-500'}`}>
                             #{player.rank}
                           </span>
                         </td>
                         <td className="py-4 px-4">
                           <div className="flex items-center gap-3">
                             <img 
                               src={`${BACKEND_URL}/api/user/avatar/${player.username}`} 
                               alt="avatar" 
                               className="w-8 h-8 rounded-lg border border-[#2A2D31] shadow-sm" 
                               onError={(e) => { e.target.onerror = null; e.target.src = "/default-avatar.png"; }}
                             />
                             <span className="text-white font-bold">{player.username}</span>
                           </div>
                         </td>
                         <td className="py-4 px-4 text-right">
                            <span className="text-[#2ecf6e] mm-num font-bold bg-[#16A34A]/10 px-3 py-1 rounded-lg border border-[#16A34A]/20">
                              {player.count} Users
                            </span>
                         </td>
                       </tr>
                     ))}
                     {leaderboard.top_10.length === 0 && (
                       <tr>
                         <td colSpan="3" className="py-8 text-center text-zinc-500 font-medium">No affiliates yet. Be the first!</td>
                       </tr>
                     )}
                   </tbody>
                 </table>
               </div>
               
               {/* Current User Rank at the Bottom */}
               {leaderboard.current_user && (
                 <div className="mt-4 p-4 bg-[#B059E8]/10 border border-[#B059E8]/30 rounded-xl flex justify-between items-center shadow-inner">
                   <div className="flex items-center gap-4">
                     <span className="text-[#c98bf0] font-black text-xl drop-shadow-[0_0_8px_rgba(176,89,232,0.5)]">
                       #{leaderboard.current_user.rank > 0 ? leaderboard.current_user.rank : '-'}
                     </span>
                     <img 
                       src={`${BACKEND_URL}/api/user/avatar/${user?.site_username}`} 
                       alt="my avatar" 
                       className="w-8 h-8 rounded-lg border-2 border-[#B059E8]/50" 
                       onError={(e) => { e.target.onerror = null; e.target.src = "/default-avatar.png"; }}
                     />
                     <span className="text-white font-bold text-base">You ({user?.site_username})</span>
                   </div>
                   <div className="text-[#2ecf6e] mm-num font-bold bg-[#16A34A]/10 px-4 py-1.5 rounded-lg border border-[#16A34A]/20">
                     {leaderboard.current_user.count} Users
                   </div>
                 </div>
               )}
            </div>

          </main>
        </div>
      </div>
    </div>
  );
};